import firebase from '@/plugins/firebase';
import moment from 'moment';
import { MUTATE } from '@/store/modules/mutation_types';
import { generateRandomId } from '@/utils/math';
import StudioService from '@/services/StudioService';
import Cookies from 'js-cookie';
import merge from 'lodash/merge';
import WarRoomService from '@/services/WarRoomService';
import { SAC_TYPE } from '@/mixins/sac-type-model';
import { FILTER_NAMES } from '../cards/constants';
import * as Sentry from '@sentry/browser';

const db = firebase.firestore();

function getUniverseIsStaging(data) {
  if (!('isStaging' in data)) {
    return undefined;
  }

  return data.isStaging;
}

function setUniverseEnvironmentState(data, commit) {
  const isStaging = getUniverseIsStaging(data);

  if (isStaging !== undefined) {
    commit('MUTATE', {
      property: 'isStaging',
      with: isStaging,
    });
  }

  if ('apiToken' in data) {
    commit('MUTATE', {
      property: 'apiToken',
      with: data.apiToken,
    });
  }

  if ('integrationToken' in data) {
    commit('MUTATE', {
      property: 'integrationToken',
      with: data.integrationToken,
    });
  }
}

export function getDashboardsByUniverseId(universeId) {
  return db
    .collection('universes')
    .doc(universeId)
    .collection('dashboards');
}

export function getDashboardByUniAndDashId({ getters }, id) {
  if (!getters.directAccess) {
    return;
  }

  const dashboardData = db
    .collection('universes')
    .doc(id.universeId)
    .collection('dashboards')
    .doc(id.dashboardId)
    .get();

  return dashboardData;
}

function getDashboardsEnvByUniverseId(universeId) {
  return db
    .collection('universes')
    .doc(universeId);
}

function getPublicUsersCollection() {
  return db
    .collection('publicUsers');
}

function getAndSetUniverseEnvironment(universeId, commit) {
  return getDashboardsEnvByUniverseId(universeId).get().then(function(doc) {
    if (doc.exists) {
      const data = doc.data();
      console.log('Document data:', data);
      setUniverseEnvironmentState(data, commit);
    } else {
      console.error('No such document!');
    }
  }).catch(function(error) {
    console.error('Error getting document:', error);
  });
}

export function deleteImage({}, item) {
  const storageRef = firebase.storage().ref(item);
  storageRef.delete().then(() => {
  }).catch((error) => {
    if (error.code !== 'storage/object-not-found') {
      Sentry.captureException(error);
      throw new Error(error);
    }
  });
}

function getBlob(obj) {
  const storageRef = firebase.storage().ref(obj);

  return storageRef.getDownloadURL()
    .then((url) => {
      return fetch(url)
        .then((response) => response.blob());
    })
    .catch((error) => {
      if (error.code !== 'storage/object-not-found') {
        Sentry.captureException(error);
        throw new Error(error);
      }
    });
}

export async function duplicateImage({}, obj) {
  const storageRef = firebase.storage().ref(obj.newId);
  const imageBlob = await getBlob(obj.oldId);

  if (imageBlob) {
    const uploadTask = storageRef.put(imageBlob);
    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        null,
        (error) => {
          reject(error);
          Sentry.captureException(error);
          throw new Error(error);
        },
        () => {
          uploadTask.snapshot.ref.getDownloadURL().then((downloadURL) => {
            resolve(downloadURL);
          });
        }
      );
    });
  } return obj.actualImage;
}

export function uploadImage({}, obj) {
  const file = obj.event.target.files[0];
  const storageRef = firebase.storage().ref(obj.cardId);
  const uploadTask = storageRef.put(file);

  return new Promise((resolve, reject) => {
    uploadTask.on('state_changed',
      null,
      (error) => {
        reject(error);
        Sentry.captureException(error);
        throw new Error(error);
      },
      () => {
        uploadTask.snapshot.ref.getDownloadURL().then((downloadURL) => {
          resolve(downloadURL);
        });
      }
    );
  });
}

async function getUserDashboards({ commit, rootState }, universeId) {
  const dashboardQuery = getDashboardsByUniverseId(universeId);

  if (rootState.user && rootState.user.user && rootState.user.user.email) {
    try {
      const myDirectAccessDashboardsQuery = dashboardQuery.where('userEmails', 'array-contains', rootState.user.user.email);

      const myDirectAccessDashboards = await myDirectAccessDashboardsQuery.get();
      if (myDirectAccessDashboards.docs.length) {
        commit('ADD_DASHBOARDS', [].concat(myDirectAccessDashboards.docs));
      }

      // Listen for realtime updates
      myDirectAccessDashboardsQuery.onSnapshot((newDash) => {
        if (newDash.docs.length) {
          commit('ADD_DASHBOARDS', [].concat(newDash.docs));
        } else {
          commit('REMOVE_DIRECT_ACCESS_DASHBOARDS');
        }
      });
    } catch (err) {
      console.warn('Dashboards with direct access', err);
    }
  }
}

async function getPublicDashboard({ commit }, universeId, publishedToken) {
  if (!publishedToken) {
    return;
  }

  const dashboardQuery = getDashboardsByUniverseId(universeId);

  try {
    const myPublicAccessDashboardsQuery = dashboardQuery
      .where('isPublished', '==', true)
      .where('publishedToken', '==', publishedToken);

    const myPublicAccessDashboards = await myPublicAccessDashboardsQuery.get();

    if (myPublicAccessDashboards.docs.length) {
      commit('ADD_DASHBOARDS', [].concat(myPublicAccessDashboards.docs));
      // chrome requires 'sameSite: 'None', Secure: true' in order to send cookies through an iframe
      // see https://github.com/js-cookie/js-cookie/issues/652
      Cookies.set('dashboard-token', publishedToken, { expires: 3 / 24, sameSite: 'None', Secure: true });
    }

    // Listen for realtime updates
    myPublicAccessDashboardsQuery.onSnapshot((newDash) => {
      if (newDash.docs.length) {
        commit('ADD_DASHBOARDS', [].concat(newDash.docs));
      } else {
        // If update to no public dashboard remove all published dashboard
        commit('REMOVE_PUBLIC_DASHBOARD');
        Cookies.remove('dashboard-token');
      }
    });

    return myPublicAccessDashboards.docs;
  } catch (err) {
    console.warn('Dashboards with token', err);
    Cookies.remove('dashboard-token');
  }
}

export async function getIfUniverseIsStaging({ commit }, universeId) {
  try {
    return getAndSetUniverseEnvironment(universeId, commit);
  } catch (err) {
    console.warn('Dashboards with direct access', err);
  }
}

export async function getDashboards({ state, ...store }, { universeId, token }) {
  await Promise.all([
    getUserDashboards(store, universeId),
    getPublicDashboard(store, universeId, token),
  ]);

  console.log('Dashboards found', state.dashboards);

  return Promise.resolve(state.dashboards);
}

export function turnOffPublish({ rootState, getters }) {
  if (!getters.directAccess) {
    return;
  }

  const dashboardDocQuery = getDashboardsByUniverseId(rootState.filter.universeId).doc(getters.activeDashboard.id);
  return dashboardDocQuery.set({
    publishedAt: 0,
    publishedToken: '',
    publishedBy: '',
    isPublished: false,
  }, {
    merge: true,
  });
}

export async function turnOnPublish({ rootState, getters }) {
  if (!getters.directAccess) {
    return;
  }

  const dashboardDocQuery = getDashboardsByUniverseId(rootState.filter.universeId).doc(getters.activeDashboard.id);
  return dashboardDocQuery.set({
    publishedAt: moment().format('x'),
    publishedToken: generateRandomId(),
    publishedBy: `${rootState.user.user.name} (${rootState.user.user.email})`,
    isPublished: true,
  }, {
    merge: true,
  });
}

export async function saveDashboardColor({ rootState, getters }, color) {
  if (!getters.directAccess) {
    return;
  }

  const dashboardDocQuery = getDashboardsByUniverseId(rootState.filter.universeId).doc(getters.activeDashboard.id);
  return dashboardDocQuery.set({
    color,
  }, {
    merge: true,
  });
}

export async function updateUserEmails({ rootState, getters }, userEmails) {
  if (!getters.directAccess) {
    return;
  }

  const dashboardDocQuery = getDashboardsByUniverseId(rootState.filter.universeId).doc(getters.activeDashboard.id);
  return dashboardDocQuery.set({
    userEmails,
  }, {
    merge: true,
  });
}

export async function updateUsers({ rootState, getters }, users) {
  if (!getters.directAccess) {
    return;
  }

  const dashboardDocQuery = getDashboardsByUniverseId(rootState.filter.universeId).doc(getters.activeDashboard.id);
  return dashboardDocQuery.set({
    users,
  }, {
    merge: true,
  });
}

export async function saveNewIsDarkTheme({ rootState, getters }, isDarkTheme) {
  if (!getters.directAccess) {
    return;
  }

  const dashboardDocQuery = getDashboardsByUniverseId(rootState.filter.universeId).doc(getters.activeDashboard.id);
  return dashboardDocQuery.set({
    isDarkTheme,
  }, {
    merge: true,
  });
}

export async function saveNewLogo({ rootState, getters }, logo) {
  if (!getters.directAccess) {
    return;
  }

  const dashboardDocQuery = getDashboardsByUniverseId(rootState.filter.universeId).doc(getters.activeDashboard.id);
  return dashboardDocQuery.set({
    logo,
  }, {
    merge: true,
  });
}

export async function saveNewCards({ rootState, getters }, cards) {
  if (!cards || !getters.directAccess) {
    return;
  }

  const dashboardDocQuery = getDashboardsByUniverseId(rootState.filter.universeId).doc(getters.activeDashboard.id);

  return dashboardDocQuery.set({
    cards,
  }, {
    merge: true,
  });
}

export async function saveTitle({ rootState, getters }, title) {
  if (!getters.directAccess) {
    return;
  }

  const dashboardDocQuery = getDashboardsByUniverseId(rootState.filter.universeId).doc(getters.activeDashboard.id);
  return dashboardDocQuery.set({
    title,
  }, {
    merge: true,
  });
}

export async function saveDescription({ rootState, getters }, description) {
  if (!getters.directAccess) {
    return;
  }

  const dashboardDocQuery = getDashboardsByUniverseId(rootState.filter.universeId).doc(getters.activeDashboard.id);
  return dashboardDocQuery.set({
    description,
  }, {
    merge: true,
  });
}

export async function saveLanguage({ rootState, getters }, language) {
  if (!getters.directAccess) {
    return;
  }

  const dashboardDocQuery = getDashboardsByUniverseId(rootState.filter.universeId).doc(getters.activeDashboard.id);
  return dashboardDocQuery.set({
    language,
  }, {
    merge: true,
  });
}

export async function savePage({ rootState, getters }, pages) {
  if (!getters.directAccess) {
    return;
  }

  const dashboardDocQuery = getDashboardsByUniverseId(rootState.filter.universeId).doc(getters.activeDashboard.id);
  return dashboardDocQuery.set({
    pages,
  }, {
    merge: true,
  });
}

export async function saveChangeControl({ rootState, getters }, changeControl) {
  if (!getters.directAccess) {
    return;
  }

  const dashboardDocQuery = getDashboardsByUniverseId(rootState.filter.universeId).doc(getters.activeDashboard.id);
  return dashboardDocQuery.set({
    changeControl,
  }, {
    merge: true,
  });
}

export async function saveContractInfo({ rootState, getters }, contracts) {
  if (!getters.directAccess) {
    return;
  }

  const dashboardDocQuery = getDashboardsByUniverseId(rootState.filter.universeId).doc(getters.activeDashboard.id);
  return dashboardDocQuery.set({
    contracts,
  }, {
    merge: true,
  });
}

const isSentimentThemeTagSelected = (filter) => {
  return Boolean(filter.polarities?.value?.length) ||
    Boolean(filter.themes?.value?.length) ||
    Boolean(filter.tags?.value?.length);
};

const getPages = (filter, getters) => {
  return !isSentimentThemeTagSelected(filter)
    ? getters.pagesIds
    : getters.pages.filter(({ channel }) => channel !== 'TikTok').map(({ id }) => id);
};

const getChannels = (filter, getters) => {
  return !isSentimentThemeTagSelected(filter)
    ? getters.pagesChannels
    : getters.pagesChannels.filter(channel => channel !== 'TikTok');
};

export function populateBasicFilter({ getters, rootState }, currentFilter) {
  const filter = {
    ...currentFilter,
    is_sac: true,
  };
  if (!rootState.filter.globalFilter.sac_type?.value) {
    // FIXME - STD-67 ITSM-13890
    const sacTypeValue = filter.sac_type?.value || ['All'];
    filter.sac_type = { value: sacTypeValue };
  }

  if (!filter.pages && !rootState.filter.globalFilter.pages) {
    filter.channels = { value: getChannels(filter, getters) };
    filter.pages = { value: getPages(filter, getters) };
  }

  return filter;
}

export function getAllSacType() {
  const sacTypeList = [];
  Object.keys(SAC_TYPE).forEach(function(key) {
    sacTypeList.push(key);
  });
  return sacTypeList;
}

export async function saveGlobalFilter({ rootState, getters, dispatch, commit }, globalFilter) {
  if (!getters.directAccess) {
    return;
  }

  const allSacTypeList = getAllSacType();

  const cards = await Promise.all(getters.cards.map(async (card) => {
    const newCardFilter = await dispatch('filter/applyGlobalFilter', { card }, { root: true });
    if (newCardFilter.hasOwnProperty(FILTER_NAMES.SAC_TYPE)) {
      if (newCardFilter.sac_type.value[0] === 'All') {
        newCardFilter.sac_type.value = allSacTypeList;
      }
    }

    card.props.filterId = await WarRoomService.encodeFilter({
      account_id: rootState.filter.accountId,
      is_staging: rootState.dashboard.isStaging,
      filter: JSON.stringify(newCardFilter),
    });

    return card;
  }));

  const dashboardDocQuery = getDashboardsByUniverseId(rootState.filter.universeId).doc(getters.activeDashboard.id);
  dashboardDocQuery.update({
    cards,
    globalFilter,
  });

  commit('misc/MUTATE', {
    property: 'forceRefresh',
    with: true,
  }, { root: true });

  setTimeout(() => {
    commit('misc/MUTATE', {
      property: 'forceRefresh',
      with: false,
    }, { root: true });
  }, 200);
}

export function getConnectedPages({ dispatch, rootState }) {
  const isStaging = rootState.dashboard.isStaging;

  const service = new StudioService(isStaging);

  return service.getConnectedPages({
    acc_id: rootState.filter.accountId,
    uni_id: rootState.filter.universeId,
  })
    .then(data => {
      if (data && data.message && data.message.length) {
        dispatch('savePage', data.message);
        return Promise.resolve(data.message);
      } else if (data.message.length === 0) {
        console.log('No connected pages found');
        return Promise.resolve([]);
      }
      return Promise.reject(data.status);
    }).catch((e) => {
      console.error('Failed to update connected pages');
      console.error(e);
      return Promise.reject(e);
    });
}

export function getUniverseInfo({ commit, rootState, getters }) {
  const isStaging = rootState.dashboard.isStaging;

  const service = new StudioService(isStaging);

  return service.getUniverseInfo({
    acc_id: rootState.filter.accountId,
    uni_id: rootState.filter.universeId,
  })
    .then(data => {
      if (data && data.message) {
        const filter = {
          ...getters.activeDashboardFilters,
          ...data.message,
        };

        commit('MUTATE', {
          property: 'dashboardFilters',
          with: filter,
        });

        return Promise.resolve(data.message);
      }
      return Promise.reject(data.status);
    }).catch((e) => {
      console.error('Failed to update groups, themes and tags');
      console.error(e);
      return Promise.reject(e);
    });
}

export function getRepliers({ rootState, getters, commit }) {
  const isStaging = rootState.dashboard.isStaging;

  const service = new StudioService(isStaging);

  return service.getRepliers({
    acc_id: rootState.filter.accountId,
  })
    .then(data => {
      if (data && data.message) {
        const filter = {
          ...getters.activeDashboardFilters,
          repliers: [...data.message],
        };

        commit('MUTATE', {
          property: 'dashboardFilters',
          with: filter,
        });

        return Promise.resolve(data.message);
      }
      return Promise.reject(data.status);
    }).catch((e) => {
      console.error('Failed to load repliers');
      console.error(e);
      return Promise.reject(e);
    });
}

export async function setActiveDashboard({ commit, getters, dispatch }, newDashboard) {
  commit('MUTATE', {
    property: 'activeDashboardIndex',
    with: newDashboard,
  });

  await dispatch('checkForChangeLogs', getters.activeDashboard);
}

export function checkForChangeLogs({ commit, getters, dispatch }, dashboard) {
  commit(`misc/${MUTATE}`, {
    property: 'hasInitialData',
    with: false,
  }, { root: true });

  const matchingHandler = (card) => (match) => {
    const reducer = (objMatch, objCard) => (isMatch, prop) => {
      if (!isMatch) {
        return false;
      }

      if (typeof match[prop] === 'object') {
        return Object.keys(match[prop]).reduce(reducer(match[prop], card[prop]), true);
      }

      return objMatch[prop] === objCard[prop];
    };

    return Object.keys(match).reduce(reducer(match, card), true);
  };

  const cards = dashboard?.data.cards || [];
  let changeControl;

  getters.changes.forEach((change) => {
    cards.forEach((card, index) => {
      const hasChange = Boolean(change.match.filter(matchingHandler(card)).length);

      if (!hasChange) {
        return;
      }

      cards[index] = merge({}, card, change.change);
    });
    changeControl = change.control;
  });

  if (changeControl) {
    dispatch('saveNewCards', cards);
    dispatch('saveChangeControl', changeControl);
  }

  commit(`misc/${MUTATE}`, {
    property: 'hasInitialData',
    with: true,
  }, { root: true });
}

export function savePublicUser({}, { email }) {
  getPublicUsersCollection().add({
    email,
    created_at: new Date().getTime(),
  });
}

export async function updateUserOnboardingStatus({ rootState, getters }, users) {
  if (!getters.directAccess) {
    return;
  }

  const dashboardDocQuery = getDashboardsByUniverseId(rootState.filter.universeId).doc(getters.activeDashboard.id);

  return dashboardDocQuery.set({
    users,
  }, {
    merge: true,
  });
}

export async function updateOnboardingStatus({ rootState, getters }, onboardingStatus) {
  if (!getters.directAccess) {
    return;
  }

  const dashboardDocQuery = getDashboardsByUniverseId(rootState.filter.universeId).doc(getters.activeDashboard.id);

  return dashboardDocQuery.set({
    onboardingStatus,
  }, {
    merge: true,
  });
}

export async function updateDashboard({ getters, rootState }, {
  dashboard,
}) {
  if (!getters.directAccess) {
    return;
  }
  try {
    const dashboardDocQuery = getDashboardsByUniverseId(rootState.filter.universeId).doc(getters.activeDashboard.id);
    return dashboardDocQuery.set({
      ...dashboard,
    }, {
      merge: true,
    });
  } catch (err) {
    console.error(err);
  }
}

export async function saveOnboardingData({ rootState, getters }, onboardingInformation) {
  if (!getters.directAccess) {
    return;
  }

  const dashboardDocQuery = getDashboardsByUniverseId(rootState.filter.universeId).doc(getters.activeDashboard.id).collection('onboarding').doc(rootState.user.user.email);

  return dashboardDocQuery.set({
    ...onboardingInformation,
  }, {
    merge: true,
  });
}

export async function saveNewCustomCard({ getters, dispatch }, newCard) {
  if (!getters.directAccess) {
    return;
  }

  const customCards = await dispatch('getCustomCards');

  customCards.components = [...customCards.components, newCard];

  try {
    const docRef = db
      .collection('cards')
      .doc('custom');

    await docRef.set({
      components: customCards.components,
    }, {
      merge: true,
    });
  } catch (error) {
    console.error(error);
  }
}

export async function getCustomCards({ getters }) {
  if (!getters.directAccess) {
    return;
  }

  try {
    const doc = await db
      .collection('cards')
      .doc('custom')
      .get();

    if (doc.exists) {
      return doc.data();
    }
    return { components: [] };
  } catch (error) {
    console.error('Can not get custom cards.', error);
  }
}

export async function saveViewTags({ rootState, getters }, { tagsView, itemId }) {
  const dashboardDocQuery = getDashboardsByUniverseId(rootState.filter.universeId).doc(getters.activeDashboard.id);

  const dashboardDoc = await dashboardDocQuery.get();
  if (!dashboardDoc.exists) {
    throw new Error('Dashboard document does not exist.');
  }

  const cards = dashboardDoc.data().cards || [];

  const updatedCards = cards.map(card =>
    card.id === itemId ? { ...card, tagsView } : card
  );

  return dashboardDocQuery.set({
    cards: updatedCards,
  }, { merge: true });
}
